// Feature 011 — the family feedback board (REQ-FEEDBACK-001/002). Pure model: validate a submission,
// and shape stored rows into a newest-first list with a human status. No side effects, no DOM.

export type FeedbackStatus = "open" | "done" | "declined";
export type FeedbackCategory = "bug" | "feature" | "other";
export const FEEDBACK_MAX = 2000;

/** The "What is this?" segment (mirrors MyTrolley): sets the reader's expectation. `icon` is a
 *  Household-Ops icon name; `placeholder` drives the textarea prompt for the chosen segment. */
export const FEEDBACK_CATEGORIES: ReadonlyArray<{ value: FeedbackCategory; label: string; icon: string; placeholder: string }> = [
  { value: "bug", label: "Bug", icon: "warning", placeholder: "What went wrong? What were you doing?" },
  { value: "feature", label: "Idea", icon: "sparkle", placeholder: "What would make this app better for the family?" },
  { value: "other", label: "Just saying", icon: "note", placeholder: "Share an idea to improve this app…" },
];

/** A stored idea (mirrors public.feedback), optionally with the author's display name joined in. */
export interface FeedbackRow {
  id: string;
  body: string;
  status: FeedbackStatus | string;
  category?: FeedbackCategory | string | null;
  created_at: string;
  author?: string | null;
}

export interface FeedbackItem {
  id: string;
  body: string;
  status: FeedbackStatus;
  statusLabel: string; // "Open" / "Done" / "Won't do"
  statusTone: "open" | "done" | "declined";
  category: FeedbackCategory;
  categoryLabel: string; // "Bug" / "Idea" / "Just saying"
  author: string | null;
  createdAt: string;
}

const LABEL: Record<FeedbackStatus, string> = { open: "Open", done: "Done", declined: "Won't do" };
const CAT_LABEL: Record<FeedbackCategory, string> = { bug: "Bug", feature: "Idea", other: "Just saying" };

/** Normalise any stored category to a known one (defaults to other). */
export function normCategory(c: string | null | undefined): FeedbackCategory {
  return c === "bug" || c === "feature" ? c : "other";
}

/** Normalise any stored status to a known one (defaults to open). */
export function normStatus(s: string | null | undefined): FeedbackStatus {
  return s === "done" || s === "declined" ? s : "open";
}

export function statusLabel(s: string | null | undefined): string {
  return LABEL[normStatus(s)];
}

/** Validate a submission. Trims; requires 1..FEEDBACK_MAX chars. Returns the cleaned value or a reason. */
export function validateFeedback(input: string | null | undefined): { ok: boolean; value?: string; error?: string } {
  const body = (input ?? "").trim();
  if (!body) return { ok: false, error: "Write your idea first." };
  if (body.length > FEEDBACK_MAX) return { ok: false, error: `Keep it under ${FEEDBACK_MAX} characters.` };
  return { ok: true, value: body };
}

/** Shape stored rows into the display list: newest first, with status metadata. Pure. */
export function feedbackListModel(rows: FeedbackRow[]): FeedbackItem[] {
  return (rows ?? [])
    .filter((r) => r && typeof r.id === "string")
    .slice()
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .map((r) => {
      const status = normStatus(r.status);
      const category = normCategory(r.category);
      return {
        id: r.id,
        body: r.body ?? "",
        status,
        statusLabel: LABEL[status],
        statusTone: status,
        category,
        categoryLabel: CAT_LABEL[category],
        author: r.author ?? null,
        createdAt: r.created_at,
      };
    });
}
